/** The table shell every list screen uses.
 *
 * Four hand-rolled copies of `panel > table-wrap > table` plus loading/error/empty conditionals
 * had drifted apart across Doors, Alerts, Audit and Watchdog (docs/FRONTEND-AUDIT.md F-P3-1).
 * That was harmless while the tables were static; the moment pagination and filters arrive it
 * becomes four places to add them and four chances to get one wrong. Extracting it AT the point
 * that duplication would have multiplied is the whole reason it is extracted now.
 *
 * The states are deliberately mutually exclusive and deliberately ordered. Error beats empty:
 * "nothing has been recorded yet" shown when the request actually FAILED is a lie that reads
 * like reassurance, and on a security console the difference between "no alerts" and "we could
 * not fetch the alerts" is the difference between going home and not.
 */
import type { ReactNode } from "react";

interface DataTableProps {
  /** Screen-reader description of the table. Every table gets one. */
  caption: string;
  columns: ReactNode;
  children: ReactNode;
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  loadingMessage?: string;
  errorMessage?: string;
  emptyMessage?: string;
  /** Rendered above the table: filters, a search box, a freshness indicator. */
  toolbar?: ReactNode;
  /** Rendered below the table, inside the same panel: the pager. */
  footer?: ReactNode;
}

export function DataTable({
  caption,
  columns,
  children,
  isLoading,
  isError,
  isEmpty,
  loadingMessage = "Loading…",
  errorMessage = "This could not be loaded. It will retry automatically.",
  emptyMessage = "Nothing to show.",
  toolbar,
  footer,
}: DataTableProps) {
  return (
    <div className="panel">
      {toolbar ? <div className="table-toolbar">{toolbar}</div> : null}

      <div className="table-wrap">
        <table>
          <caption className="visually-hidden">{caption}</caption>
          <thead>
            <tr>{columns}</tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>

      {/* `role="status"` so a screen reader is told when a list finishes loading, rather than
          being left on a table whose rows changed underneath it. */}
      {isLoading ? (
        <div className="loading" role="status">
          {loadingMessage}
        </div>
      ) : null}

      {/* Error takes precedence over empty: a failed request rendered as "nothing here yet"
          reads like an all-clear. */}
      {!isLoading && isError ? (
        <div className="empty" role="alert">
          {errorMessage}
        </div>
      ) : null}

      {!isLoading && !isError && isEmpty ? <div className="empty">{emptyMessage}</div> : null}

      {footer ? <div className="table-footer">{footer}</div> : null}
    </div>
  );
}
